/* Repository path: frontend/src/components/SupportPrompt.tsx */

import { useEffect, useId, useRef, useState } from "react";
import { fetchSupportLink } from "../api/support";
import type { SupportPlacement } from "../types";
import { PRODUCT_NAME } from "../utils/productName";
import { Button } from "./Button";

// W4-09 open question 1 is resolved by W6-02 (D-W6-02-03): the one on-screen name
// is `PRODUCT_NAME` ("Blauwe"), imported from `utils/productName.ts` and shared
// with the top bar, the route titles and the feedback panel.

type Phase = "pending" | "resting" | "dismissed" | "absent";

interface ResolvedLink {
  providerLabel: string;
  url: string;
}

export interface SupportPromptProps {
  /** Which surface this instance sits on — varies only the wrapper's
   * placement styling and never anything sent to the server. */
  placement: SupportPlacement;
  announce: (message: string) => void;
}

/**
 * The voluntary, dismissible, external support link (W4-09). Renders
 * nothing at all until a `GET /api/v1/support` fetch resolves to an
 * enabled, allowlisted link, and nothing forever if it does not — see
 * docs/architecture/features/W4-09.md and
 * docs/architecture/adr/ADR-0013-external-support-link-boundary.md. Never
 * a modal, a route or a viewport-pinned element; never awaited by a
 * parent; never on the critical path of anything. Writes nothing to
 * `localStorage` or `sessionStorage` — dismissal is in-memory only, so the
 * block returns on a later visit, exactly like the W4-08 feedback panel's
 * own resting-state behaviour.
 */
export function SupportPrompt({ placement, announce }: SupportPromptProps) {
  const baseId = useId();
  const titleId = `${baseId}-title`;

  const [phase, setPhase] = useState<Phase>("pending");
  const [link, setLink] = useState<ResolvedLink | null>(null);
  const dismissedRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    fetchSupportLink(controller.signal).then((result) => {
      if (!active) return;
      if (result.enabled) {
        setLink({ providerLabel: result.provider_label, url: result.url });
        setPhase("resting");
      } else {
        setPhase("absent");
      }
    });
    return () => {
      active = false;
      controller.abort();
    };
    // Fetched exactly once per mount: the effect body references only the
    // local `controller`/`active` and the stable, module-level
    // `fetchSupportLink` import — never `placement`, which is used only
    // in the JSX below — so an empty dependency array is already correct
    // and needs no exhaustive-deps override.
  }, []);

  useEffect(() => {
    if (phase === "dismissed") dismissedRef.current?.focus();
  }, [phase]);

  if (phase === "pending" || phase === "absent" || !link) return null;

  if (phase === "dismissed") {
    return (
      <p
        className="support-prompt support-prompt--dismissed"
        role="status"
        tabIndex={-1}
        ref={dismissedRef}
      >
        Hidden. Thanks either way.
      </p>
    );
  }

  const dismiss = () => {
    setPhase("dismissed");
    announce("Support prompt hidden");
  };

  return (
    <section
      className={`support-prompt support-prompt--${placement}`}
      aria-labelledby={titleId}
    >
      <p className="eyebrow">Optional</p>
      <h2 id={titleId}>Support {PRODUCT_NAME}</h2>
      <p>
        If {PRODUCT_NAME} saved you time figuring this out, you can support keeping it free.
        It’s entirely optional and it changes nothing about your plan or your reminders.
      </p>
      <p>
        Payments are handled by <strong>{link.providerLabel}</strong> on their own site. We
        never see your card or bank details, and we don’t receive a confirmation that you paid.
      </p>
      <div className="action-row">
        {/*
          A genuine `<a href>`, never a JavaScript-driven navigation and
          never an image or a provider-hosted button script. `target` and
          `rel` open a new tab without handing this page's own address to
          the provider as a referrer — the saved plan lives at
          `/plans/{public_id}`, and that identifier must never leave this
          origin. See ADR-0013 decision 2. `referrerPolicy` is set
          explicitly rather than only relied upon via `rel`, since some
          older engines honour only one of the two.
        */}
        <a
          className="button button--secondary support-prompt__link"
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          referrerPolicy="no-referrer"
        >
          Leave a tip on {link.providerLabel} (opens in a new tab)
        </a>
        <Button type="button" variant="quiet" onClick={dismiss}>
          No thanks
        </Button>
      </div>
    </section>
  );
}
